/**
 * @file   src\components\Onboarding\UserRegister\AgentOnboardingModal.js
 * @brief  This file is responsible for agent onboarding modal
 * @author ZCO Engineer
 * @copyright (c) 2023, ZCO
 */
import React, { useState, useEffect } from "react";
import Modal from "react-bootstrap/Modal";
import { Button } from "react-bootstrap";
import GoogleLogo from "../../../images/google-logo.svg";
import FbLoginBannerDesktop from "../../../images/fb-login-banner-desktop.png";
import passwordIcon from "../../../images/password-toggle-icon.svg";
import passwordClosedIcon from "../../../images/password-toggle-icon-closed.svg";
import {
  convertToNumber,
  emailValidation,
  passwordValidation,
} from "../../../helpers/validations";
import TextBox from "../../Common/TextBox";
import {
  LOGIN,
  POST_WANT_AD,
  SIGNUP,
  resetModalemailOnly,
} from "../../../constants/onBoarding";
import { USER_INFO_KEY, USER_TYPE_AGENT } from "../../../constants/common";
import _ from "lodash";
import {
  getLocalStorage,
  removeLocalStorage,
  setLocalStorage,
} from "../../../helpers/common";
import UserSocialSignUpModal from "../v2/UserSocialSignUpModal";
import {
  userLogin,
  userSignUp,
  verifyEmail,
} from "../../../services/v2/signupService";
import { UserSocialSignOn } from "../../../services/v2/socialSignOns";
import { toast } from "react-toastify";
import { getAuthUrl } from "../../../helpers/v2/socialSignOns";

const AgentOnboardingModal = (props) => {
  const {
    handleUserTypeModal,
    handleAgentOnboardingModal,
    agentOnboardingPopup,
    handleResetPwdModal,
    handleUserLogin,
  } = props;
  const isShowPopup = _.get(agentOnboardingPopup, "params.showPopup", "");
  const userAction = _.get(agentOnboardingPopup, "params.userAction", "");
  const utm_source = _.get(agentOnboardingPopup, "params.utm_source", "");
  const utm_medium = _.get(agentOnboardingPopup, "params.utm_medium", "");
  const utm_campaign = _.get(agentOnboardingPopup, "params.utm_campaign", "");
  const isLogin = userAction === LOGIN;
  const isPostWantAd =
    _.get(agentOnboardingPopup, "params.userPreference", null) === POST_WANT_AD;

  const initialFormData = {
    email: "",
    pwd: "",
    confPwd: "",
  };
  const initialErrors = {
    emailError: "",
    pwdError: "",
    confPwdError: "",
  };

  const [formData, setFormData] = useState(initialFormData);
  const [formErrors, setFormErrors] = useState(initialErrors);
  const [buttonDisable, setButtonDisable] = useState(true);

  const [isShowPassWord, setPassWordShow] = useState(false);
  const [passwordType, setPassWordType] = useState("password");

  const [loginError, setLoginError] = useState(null);
  const [signUpWithEmail, setSignUpWithEmail] = useState(true);
  const [authCode, setAuthCode] = useState(null);
  const [platform, setPlatform] = useState(getLocalStorage("provider") || null);
  const [isLoading, setIsLoading] = useState(false);

  const onTextChange = (e) => {
    setLoginError(null);
    const textChangeField = e.target.name;
    setFormErrors((formErrors) => ({
      ...formErrors,
      [`${textChangeField}Error`]: "",
    }));
    const value = e.target.value;
    setFormData((formData) => ({ ...formData, [e.target.name]: value }));
  };

  useEffect(() => {
    // Extract code from URL immediately
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get("code");
    const error = urlParams.get("error");

    if (code && !window.opener) {
      localStorage.setItem("auth_code", code);
      setAuthCode(code);
      self.close();
    }

    if (error) {
      console.error("OAuth Error:", error);
      window.close();
    }
  }, []);

  useEffect(() => {
    const handleStorageChange = (event) => {
      if (event.key === "auth_code" && event.newValue) {
        setAuthCode(event.newValue);
        setIsLoading(true);
        removeLocalStorage("auth_code");
      }
    };

    // Add event listener for storage event
    window.addEventListener("storage", handleStorageChange);

    // Cleanup event listener on component unmount
    return () => {
      window.removeEventListener("storage", handleStorageChange);
    };
  }, []);

  useEffect(() => {
    const handleAuthCode = async () => {
      if (authCode) {
        try {
          const response = await UserSocialSignOn({
            code: authCode,
            provider: platform,
            isFromMobile: false,
          });
          updateUserDetailsToStorage(response, platform);
          handleAgentOnboardingModal({ showPopup: false, userAction: null });
          handleUserLogin();
          setIsLoading(false);
        } catch (error) {
          toast.error(
            `An error occurred during ${platform} login. Please try again.`,
          );
          setIsLoading(false);
        }
      }
    };

    handleAuthCode();
  }, [authCode]);

  useEffect(() => {
    const email = _.get(formData, "email", null);
    const pwd = _.get(formData, "pwd", null);
    const confPwd = _.get(formData, "confPwd", null);
    let disableButton;
    if (isLogin) {
      if (email && pwd) {
        disableButton = false;
      } else {
        disableButton = true;
      }
    } else {
      if (email && pwd && confPwd) {
        disableButton = false;
      } else {
        disableButton = true;
      }
    }
    setButtonDisable(disableButton);
  }, [formData]);

  useEffect(() => {
    if (isShowPassWord) {
      setPassWordType("text");
    } else {
      setPassWordType("password");
    }
  }, [isShowPassWord]);

  useEffect(() => {
    setPassWordShow(false);
    setFormErrors(initialErrors);
    setFormData(initialFormData);
  }, [userAction]);
  /**
   * To handle verify email
   * @param {*} email
   * @returns
   */
  const checkEmailExist = async (email) => {
    const userEmail = {
      Email: email,
    };
    return await verifyEmail(userEmail).then((response) => {
      return response?.result;
    });
  };
  /**
   * To handle validation
   * @returns
   */
  const validate = async () => {
    setFormErrors(initialErrors);
    let isValid = true;
    const email = _.get(formData, "email", null).trim();
    const pwd = _.get(formData, "pwd", null).trim();
    const passWordError = passwordValidation(pwd);

    if (!emailValidation(email)) {
      setFormErrors((formErrors) => ({
        ...formErrors,
        emailError: "InValid Email",
      }));
      isValid = false;
    } else if (!isLogin && passWordError) {
      setFormErrors((formErrors) => ({
        ...formErrors,
        pwdError: passWordError,
      }));
      isValid = false;
    } else {
      const res = await checkEmailExist(email);
      const userExist = _.get(res, "UserExists", false);
      const userVerified = _.get(res, "IsVerified", false);
      const isDeleted = _.get(res, "IsDeleted", false);

      if (isLogin) {
        if (userExist && userVerified && !isDeleted) {
          isValid = true;
        } else {
          setFormErrors((formErrors) => ({
            ...formErrors,
            emailError: "Invalid user",
          }));
          isValid = false;
        }
      } else {
        if (userExist) {
          setFormErrors((formErrors) => ({
            ...formErrors,
            emailError: "Email already exists",
          }));
          isValid = false;
        } else {
          const confPwd = _.get(formData, "confPwd", null).trim();
          if (pwd !== confPwd) {
            setFormErrors((formErrors) => ({
              ...formErrors,
              confPwdError: "Password not matching",
            }));
            isValid = false;
          } else {
            isValid = true;
          }
        }
      }
    }

    return isValid;
  };
  /**
   * To handle submit
   */
  const handleSubmit = async () => {
    let reqPayload;
    if (!isLogin) {
      const isValid = await validate();

      if (isValid) {
        reqPayload = {
          email: _.get(formData, "email", ""),
          password: _.get(formData, "pwd", ""),
          confirmPassword: _.get(formData, "confPwd", ""),
          utmSource: utm_source,
          utcMedium: utm_medium,
          utcCampaign: utm_campaign,
        };
        await userSignUp(reqPayload).then((response) => {
          if (_.get(response, "errorCode", "") === 0) {
            updateUserDetailsToStorage(response);
            handleAgentOnboardingModal({ showPopup: false, userAction: null });
            handleUserTypeModal({ showModal: true });
          } else {
            setFormErrors((formErrors) => ({
              ...formErrors,
              emailError: _.get(response, "message", ""),
            }));
          }
        });
      }
    } else {
      const email = _.get(formData, "email", null).trim();
      if (!emailValidation(email)) {
        setFormErrors((formErrors) => ({
          ...formErrors,
          emailError: "InValid Email",
        }));
        return
      }
      reqPayload = {
        email: _.get(formData, "email", ""),
        password: _.get(formData, "pwd", ""),
      };
      await userLogin(reqPayload)
        .then((response) => {
          if (_.get(response, "errorCode", "") === 0) {
            updateUserDetailsToStorage(response);
            handleAgentOnboardingModal({ showPopup: false, userAction: null });
            handleUserLogin();
          } else {
            setLoginError(_.get(response, "message", ""));
          }
        })
        .catch((err) => setLoginError(err?.response?.data?.message));
    }
  };
  /**
   * To update user details to storage
   * @param {*} userResponse
   */
  const updateUserDetailsToStorage = (userResponse, provider) => {
    const resResult = _.get(userResponse, "result", null);
    const userResult = _.get(resResult, "user", null);
    const userSession = _.get(resResult, "userSession", "");
    const refreshToken = _.get(resResult, "refreshToken", "");
    const firstName = _.get(userResult, "FirstName", "") || "";
    const lastName = _.get(userResult, "LastName", "") || "";
    const name = firstName + " " + lastName;
    const tutorialStatus = _.get(resResult, "tutorialStatus", "");
    const userTypeId = _.get(userResult, "UserTypeId", "");

    if (userSession) {
      const userDetails = {
        token: {
          accessToken: userSession,
          refreshToken: refreshToken,
          providerLogin: provider ? provider : false,
        },
        userInfo: {
          userId: _.get(userResult, "UserId", ""),
          email: _.get(userResult, "Email", ""),
          name: name,
          firstName: firstName,
          lastName: lastName,
          userIsVerified: _.get(userResult, "IsVerified", ""),
          userEmailVerified: _.get(userResult, "IsEmailVerified", ""),
          planStatus: _.get(userResult, "PlanStatus", ""),
          userStatus: _.get(userResult, "UserStatus", ""),
          userType: userTypeId,
          userPreference: isPostWantAd ? POST_WANT_AD : null,
          image: _.get(userResult, "Image", ""),
        },
      };
      if (convertToNumber(userTypeId === USER_TYPE_AGENT)) {
        const agentTitle = _.get(userResult, "Agent_Title", "");
        userDetails.userInfo.agentTitle = agentTitle;
      }

      setLocalStorage(USER_INFO_KEY, userDetails);
      //Saving tutorial displayed status page
      if (tutorialStatus && tutorialStatus.length) {
        let tutorialDisplayedPages = [];
        for (let i = 0; i < tutorialStatus.length; i++) {
          if (
            tutorialStatus[i].PageNo &&
            tutorialStatus[i].Status == 1 &&
            !tutorialDisplayedPages.includes(tutorialStatus[i].PageNo)
          ) {
            tutorialDisplayedPages.push(tutorialStatus[i].PageNo);
          }
        }
        setLocalStorage("tutorialDisplayedPages", tutorialDisplayedPages);
      }
    }
  };

  const handleToggle = (action) => {
    handleAgentOnboardingModal({ showPopup: true, userAction: action });
  };

  const handlePassWordShow = () => {
    setPassWordShow(!isShowPassWord);
  };

  const openLoginPopup = async (
    url,
    popupName,
    popupWidth = 600,
    popupHeight = 600,
  ) => {
    const popupTop =
      window.top.outerHeight / 2 + window.top.screenY - popupHeight / 2;
    const popupLeft =
      window.top.outerWidth / 2 + window.top.screenX - popupWidth / 2;

    // Open a popup window
    const loginPopup = window.open(
      url,
      popupName,
      `width=${popupWidth},height=${popupHeight},top=${popupTop},left=${popupLeft}`,
    );

    if (!loginPopup) {
      throw new Error("Popup blocked. Please allow popups for this site.");
    }

    return loginPopup;
  };

  const handleOAuthLogin = async (platform) => {
    try {
      setIsLoading(true);
      const authUrl = getAuthUrl(platform); // Get the platform-specific auth URL
      setPlatform(platform === "apple-mobile" ? "apple" : platform);
      setLocalStorage("provider", platform);

      // Open popup
      const popup = await openLoginPopup(
        authUrl.toString(),
        `${platform} Login`,
      );

      const checkPopupResult = setInterval(() => {
        try {
          const popupUrl = popup.location.href;
          if (popupUrl.includes("?code=") || popupUrl.includes("?error=")) {
            const urlParams = new URLSearchParams(popup.location.search);
            const code = urlParams.get("code");

            if (code) {
              localStorage.setItem("auth_code", code);
              setAuthCode(code);
              // Close the popup
              popup.close();
              clearInterval(checkPopupResult);
            }
          }
        } catch (error) {
          // Ignore cross-origin errors until the popup redirects back
        }

        // Check if popup was closed manually
        if (popup.closed) {
          if (!localStorage.getItem("auth_code")) {
            if (platform !== "apple")
              toast.error(`${platform} login failed or cancelled`);
            setIsLoading(false);
          }
          localStorage.removeItem("auth_code");
          localStorage.removeItem("provider");
          clearInterval(checkPopupResult);
        }
      }, 500);
    } catch (error) {
      toast.error(
        error.message || "An error occurred. Please try again later.",
      );
      removeLocalStorage("auth_code");
      removeLocalStorage("provider");
      setIsLoading(false);
    }
  };

  return (
    <Modal
      show={isShowPopup}
      size="md"
      aria-labelledby="contained-modal-title-vcenter"
      centered
      backdrop="static"
      className="modal-width-548"
    >
      <Modal.Body className="pt-32 pb-32">
        <button
          type="button"
          className="btn-close"
          aria-label="Close"
          onClick={() =>
            handleAgentOnboardingModal({ showPopup: false, userAction: null })
          }
        ></button>
        {!isLogin ? (
          <>
            <h2 className="mob-style-heading text-center mb-0">
              {signUpWithEmail
                ? "Sign up for Gitcha"
                : "Login or sign up in seconds"}
            </h2>
            <div className="fb-login-banner-desktop mt-18">
              <img src={FbLoginBannerDesktop} alt="Login Banner" />
            </div>
            <div className="fb-login-tagline-desktop">
              {signUpWithEmail
                ? "Create your account to get started, it’s free!"
                : "Use your work email or another service to continue with Gitcha, it’s free!"}
            </div>
          </>
        ) : (
          <>
            <h2 className="text-center mb-0">Sign in to Gitcha</h2>
            <div>
              <p className="text-center mb-32 pt-32">
                By continuing, you agree to Gitcha's{" "}
                <a role="button" target="_blank" href="terms-of-service">
                  {" "}
                  Terms
                </a>{" "}
                and
                <a role="button" target="_blank" href="privacy-policy">
                  {" "}
                  Privacy Policy.
                </a>
              </p>
              <Button
                variant="outline-primary"
                className="btn outline-btn w-100 mb-20"
                onClick={() => handleOAuthLogin("google")}
              >
                <img src={GoogleLogo} className="me-2" />
                <span>Continue With Google</span>
              </Button>
              <div className="segment-divider">OR</div>
            </div>
          </>
        )}
        {signUpWithEmail ? (
          <>
            <div className="textbox-wrap mb-4 mb-sm-4 position-relative">
              <label className="form-label">Email</label>
              <TextBox
                name="email"
                type="email"
                tabIndex="1"
                autoFocus={true}
                errorMessage={_.get(formErrors, "emailError", "")}
                value={_.get(formData, "email", "")}
                placeholder="yourname@email.com"
                className={
                  _.get(formErrors, "emailError", null)
                    ? "form-control validation-txtfld"
                    : "form-control"
                }
                onChange={(e) => {
                  onTextChange(e);
                }}
              />
            </div>

            <div className="textbox-wrap mb-0 mb-sm-0 position-relative">
              <label className="form-label">Password</label>
              <TextBox
                name="pwd"
                type={passwordType}
                tabIndex="2"
                errorMessage={_.get(formErrors, "pwdError", "")}
                value={_.get(formData, "pwd", "")}
                placeholder={isLogin ? "Password" : "Create a Password"}
                errorClass="error-msg error-msg-height"
                className={
                  _.get(formErrors, "pwdError", null)
                    ? "pad-right-40 validation-txtfld"
                    : "pad-right-40"
                }
                onChange={(e) => {
                  onTextChange(e);
                }}
              />
              {loginError ? (
                <div className="error-msg error-msg-height">{loginError}</div>
              ) : (
                <div className="error-msg">
                  {loginError && loginError !== "" && (
                    <span className="error-msg">{loginError}</span>
                  )}
                </div>
              )}

              <Button
                variant=""
                className="btn btn-pwd-toggle"
                onClick={() => handlePassWordShow()}
              >
                <img
                  src={isShowPassWord ? passwordClosedIcon : passwordIcon}
                  alt="pwdicon"
                />
              </Button>
            </div>
            {isLogin && (
              <p className="text-start mb-32">
                <a
                  href={void 0}
                  role="button"
                  onClick={() =>
                    handleResetPwdModal({
                      showResetModal: true,
                      resetPwdModalType: resetModalemailOnly,
                    })
                  }
                >
                  Forgot your password?
                </a>
              </p>
            )}

            {!isLogin && (
              <>
                {/* <br /> */}
                <div className="textbox-wrap mb-4 mb-sm-4 position-relative">
                  <label className="form-label">Confirm Password</label>
                  <TextBox
                    name="confPwd"
                    type="password"
                    tabIndex="3"
                    errorMessage={_.get(formErrors, "confPwdError", "")}
                    value={_.get(formData, "confPwd", "")}
                    placeholder="Confirm Password"
                    className={
                      _.get(formErrors, "confPwdError", null)
                        ? "form-control validation-txtfld"
                        : "form-control"
                    }
                    onChange={(e) => {
                      onTextChange(e);
                    }}
                  />
                </div>
              </>
            )}

            <Button
              variant="primary"
              tabIndex="4"
              className="w-100 mb-4"
              disabled={buttonDisable}
              onClick={() => handleSubmit()}
            >
              Continue
            </Button>
            {!isLogin && (
              <>
                <div className="segment-divider mb-24">OR</div>
                <Button
                  variant="outline-primary"
                  className="btn outline-btn w-100 mb-20"
                  onClick={() => handleOAuthLogin("google")}
                >
                  <img src={GoogleLogo} className="me-2" />
                  <span>Continue With Google</span>
                </Button>
                <p className="text-center mb-20">
                  By signing up, you agree to Gitcha’s
                  <a role="button" target="_blank" href="terms-of-service">
                    {" "}
                    Terms
                  </a>{" "}
                  and
                  <a role="button" target="_blank" href="privacy-policy">
                    {" "}
                    Privacy Policy.
                  </a>
                </p>
              </>
            )}

            {isLogin ? (
              <p className="text-center">
                Don't have an account yet?{" "}
                <a
                  href={void 0}
                  role="button"
                  onClick={() => handleToggle(SIGNUP)}
                >
                  Sign up here
                </a>
              </p>
            ) : (
              <p className="text-center">
                Already have an account?{" "}
                <a
                  href={void 0}
                  role="button"
                  onClick={() => handleToggle(LOGIN)}
                >
                  Login here
                </a>
              </p>
            )}
          </>
        ) : (
          <UserSocialSignUpModal
            isLoading={isLoading}
            platform={platform}
            OAuthLogin={handleOAuthLogin}
            emailLogin={() => setSignUpWithEmail(true)}
          />
        )}
      </Modal.Body>
    </Modal>
  );
};
export default AgentOnboardingModal;
